import React, { useState, useEffect, useCallback, useRef } from "react";
import ManageAccessTable, { buildManageAccessRowKey } from "./ManageAccessTable";
import EditAccessForm from "./EditAccessForm";
import Modal from "../../components/common/Modal";
import { useAppSelector, useAppDispatch } from "../../store/hooks";
import { useNavigate } from "react-router-dom";
import {
  setUsers,
  fetchManageAccessUsers,
  resetManageAccessState,
  deleteAccess,
  UserAccess,
} from "../../store/slices/manageAccessSlice";
import Loader from "../../components/common/Loader";
import BreadCrumbs from "../../components/common/BreadCrumbs/BreadCrumbs";
import CommonErrorBanner from "../../components/common/CommonErrorBanner";

const MODAL_STYLES = `
  #modal-manual-trig .modal-container {
    max-width: 45% !important;
    width: 90% !important;
    top: 20% !important;
  }
`;

/**
 * Renders the Manage Access list with inline edit and delete actions.
 * @returns Manage Access page with editable user access rows.
 */
const ManageAccessWithEdit: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { users, loading, currentPage, perPage } = useAppSelector(
    (state) => state.manageAccess,
  );
  const [editingUser, setEditingUser] = useState<UserAccess | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserAccess | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editStatus, setEditStatus] = useState<'success' | 'error' | null>(null);
  const [editErrorMessage, setEditErrorMessage] = useState(
    'Something went wrong. Please try again.',
  );
  const [editErrorHeader, setEditErrorHeader] = useState('Error');
  const [editErrorVariant, setEditErrorVariant] = useState('Error');
  const formRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!editStatus) return;
    const t = setTimeout(() => setEditStatus(null), 5000);
    return () => clearTimeout(t);
  }, [editStatus]);

  // Fetch data on mount and when pagination changes
  useEffect(() => {
    const abortController = new AbortController();
    dispatch(fetchManageAccessUsers({ page: currentPage }));
    // Cleanup function to cancel request on unmount or page change
    return () => {
      abortController.abort();
    };
  }, [dispatch, currentPage]);

  const handlePageChange = useCallback(
    (p?: number) => {
      if (typeof p === "number") {
        // Reset editing state when changing pages
        setEditingUser(null);
        dispatch(fetchManageAccessUsers({ page: p }));
      }
    },
    [dispatch, perPage],
  );

  // Cleanup state on unmount
  useEffect(() => {
    return () => {
      dispatch(resetManageAccessState());
    };
  }, [dispatch]);

  // Memoized edit handler
  const handleEditClick = useCallback((user: UserAccess) => {
    setEditingUser(user);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }, []);

  const handleDeleteClick = useCallback((user: UserAccess) => {
    setDeleteTarget(user);
    setShowDeleteModal(true);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) { setShowDeleteModal(false); return; }
    setShowDeleteModal(false);
    const payload = {
      nuid: deleteTarget.nuid,
      privRole: deleteTarget.privRole,
      region: deleteTarget.responsibilityRegion ?? null,
      area: deleteTarget.responsibilityArea == null ? null : String(deleteTarget.responsibilityArea),
      center: deleteTarget.responsibilityCenter == null ? null : String(deleteTarget.responsibilityCenter),
      location: deleteTarget.responsibilityLocation == null ? null : String(deleteTarget.responsibilityLocation),
    };
    // Adjust page: if only 1 user left on this page and not on first page, go to previous page
    const targetPage = users.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
    await dispatch(deleteAccess({ payload, page: targetPage }) as any);
    // Close edit form if the deleted user is currently being edited
    if (editingUser && buildManageAccessRowKey(deleteTarget) === buildManageAccessRowKey(editingUser)) {
      setEditingUser(null);
    }
    setDeleteTarget(null);
  }, [deleteTarget, dispatch, currentPage, editingUser, users.length]);

  // Memoized cancel handler
  const handleCancel = useCallback(() => {
    setEditingUser(null);
    setTimeout(() => tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }, []);

  // Memoized save handler
  const handleSave = useCallback(
    (updated: UserAccess) => {
      const editingKey = editingUser ? buildManageAccessRowKey(editingUser) : null;
      const updatedUsers = users.map((u) =>
        buildManageAccessRowKey(u) === editingKey ? updated : u,
      );
      dispatch(setUsers(updatedUsers));
      setEditingUser(null);
      setEditStatus('success');
      setTimeout(() => bannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
    },
    [users, dispatch, editingUser],
  );

  const handleSaveError = useCallback((message?: string, header?: string, variant?: string) => {
    setEditErrorMessage(message ?? 'Something went wrong. Please try again.');
    setEditErrorHeader(header ?? 'Error');
    setEditErrorVariant(variant ?? 'Error');
    setEditStatus('error');
    setTimeout(() => bannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
  }, []);

  const handleBreadcrumbPrevious = () => {
    navigate("/home");
  };

  return (
    <div className="lg:mx-40! mx-5!">
      <BreadCrumbs
        previousPage="UBT Home"
        onPreviousClick={handleBreadcrumbPrevious}
      />
      <div>
        <div className="flex justify-between items-end ">
          <h1 className="h1 tablet:mt-8! mobile:mt-3.5!">Manage access</h1>
          <button
            className="button flex items-center group hover:text-white! focus:text-white! "
            data-analytics-type="button"
            data-analytics-click="Default"
            onClick={() => navigate("/home/manage-access/add-access")}
          >
            <img
              src="/assets/images/plus-circle-outline.svg"
              alt="add"
              className="w-6 h-6 group-focus:brightness-0! group-focus:invert!  group-hover:brightness-0! group-hover:invert!"
            />
            <span>Add User Access</span>
          </button>
        </div>
      </div>
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="mt-12.5! mb-4!">
            <div ref={bannerRef}>
              {editStatus === 'success' && (
                <CommonErrorBanner
                  variant="Success"
                  heading="Success"
                  messageInfo="This employee has been updated successfully to this role and scope of access."
                />
              )}
              {editStatus === 'error' && (
                <CommonErrorBanner
                  variant={editErrorVariant as 'Error' | 'Alert' | 'Success' | 'Informational'}
                  heading={editErrorHeader}
                  messageInfo={editErrorMessage}
                />
              )}
            </div>
            <p className="p1 -medium mt-2 md:mb-0!">Manage User Access</p>
          </div>
          <div ref={tableRef}>
            <ManageAccessTable
              onEdit={handleEditClick}
              onDelete={handleDeleteClick}
              onPageChange={handlePageChange}
              editingUserKey={editingUser ? buildManageAccessRowKey(editingUser) : null}
            />
          </div>
          {editingUser && (
            <div ref={formRef}>
              <hr className="-variation-1" />
              <h2 className="h2 mt-8! pt-8!">Editing Details</h2>
              <EditAccessForm
                key={buildManageAccessRowKey(editingUser)}
                employee={editingUser}
                onClose={handleCancel}
                onSave={handleSave}
                onError={handleSaveError}
              />
            </div>
          )}
        </>
      )}
      <Modal
        isOpen={showDeleteModal}
        title="Delete this user?"
        clearBtn="Cancel"
        submitBtn="Delete User"
        onSecondary={handleCancelDelete}
        onPrimary={handleConfirmDelete}
        onClose={handleCancelDelete}
      >
        <div className="flex items-center gap-3">
          <img
            src="/assets/images/alertSolid.svg"
            alt="alert-icon"
            className="w-10 h-10 shrink-0"
          />
          <p className="p3 -book mb-4!">
            Do you wish to remove access for this user?
          </p>
        </div>
        <style>{MODAL_STYLES}</style>
      </Modal>
    </div>
  );
};

export default ManageAccessWithEdit;
